import React, { useState, useEffect } from 'react';
import { Button, Modal } from 'react-bootstrap';
import Select from "react-select";
import axios from 'axios';
import { toast } from 'react-toastify';

const AddProductionCosting = ({ show, onClose, refreshData }) => {
    const baseURL = process.env.REACT_APP_API_BASE_URL;
    const [itemOptions, setItemOptions] = useState([]);
    const [productOptions, setProductOptions] = useState([]);
    const [selectedItemDetails, setSelectedItemDetails] = useState(null);
    const [errors, setErrors] = useState({});

    const [form, setForm] = useState({
        production_item: null,
        production_productcategory: null,
        production_rmcount: '',
        production_rmcost: '',
        production_lcost: '', 
    });

    const [totalCost, setTotalCost] = useState(0);

    useEffect(() => {
        const fetchItems = async () => {
            try {
                const res = await axios.get(`${baseURL}/manage-items/active`);
                const data = res.data?.data || [];
                const formatted = data.map(item => ({ value: item.sno, label: item.item_name, full: item }));
                setItemOptions(formatted);
            } catch (err) {
                console.error("❌ Error fetching items:", err);
            }
        };

        const fetchProductCategories = async () => {
            try {
                const response = await axios.get(`${baseURL}/product-categories/products/active`);
                const categories = response.data?.data || [];
                const formatted = categories.map(cat => ({ value: cat.sno, label: cat.productcategory_name }));
                setProductOptions(formatted);
            } catch (err) {
                console.error("❌ Error fetching product categories:", err);
            }
        };

        fetchItems();
        fetchProductCategories();
    }, []);

    useEffect(() => {
        const rm = parseFloat(form.production_rmcost) || 0;
        const lc = parseFloat(form.production_lcost) || 0;
        setTotalCost(rm + lc);
    }, [form.production_rmcost, form.production_lcost]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (parseFloat(value) < 0) return; // Prevent negative input
        setForm(prev => ({ ...prev, [name]: value }));
        setErrors(prev => ({ ...prev, [name]: '' }));
    };

    const handleSelectChange = (selected, action) => {
        const name = action.name;
        const value = selected?.value || null;
        setForm(prev => ({ ...prev, [name]: value }));
        if (name === 'production_item') setSelectedItemDetails(selected?.full || null);
        setErrors(prev => ({ ...prev, [name]: '' }));
    };

    const validate = () => {
        const newErrors = {};
        if (!form.production_item) newErrors.production_item = "Item is required";
        if (!form.production_productcategory) newErrors.production_productcategory = "Product category is required";
        if (!form.production_rmcount) newErrors.production_rmcount = "Raw material count is required";
        if (!form.production_rmcost) newErrors.production_rmcost = "Raw material cost is required";
        if (!form.production_lcost) newErrors.production_lcost = "Labour cost is required";
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validate()) return;
        try {
            const payload = {
                ...form,
                production_rmcount: parseFloat(form.production_rmcount),
                production_rmcost: parseFloat(form.production_rmcost),
                production_lcost: parseFloat(form.production_lcost),
            };
            await axios.post(`${baseURL}/production-cost`, payload);
            toast.success('Production Cost added successfully');
            if (typeof refreshData === 'function') refreshData();
            resetForm();
            onClose();
        } catch (err) {
            console.error("Error saving production cost:", err);
            toast.error('Failed to save production cost');
        }
    };

    const resetForm = () => {
        setForm({
            production_item: null,
            production_productcategory: null,
            production_rmcount: '',
            production_rmcost: '',
            production_lcost: '',
        });
        setTotalCost(0);
        setSelectedItemDetails(null);
        setErrors({});
    };

    return (
        <Modal show={show} size="lg" onHide={onClose} backdrop="static">
            <div className="modal-content">
                <div className="modal-header" style={{ backgroundColor: '#3ab384' }}>
                    <h3 className="modal-title text-white">Add Production Costing</h3>
                    <Button variant="" type="button" className="close px-6 py-2" onClick={onClose}>
                        <span className="text-white">×</span>
                    </Button>
                </div>
                <div className="modal-body">
                    <div className="row mt-4">
                        <div className="col-lg-6 mb-2">
                            <label className="text-black font-w500 fs-7">Item<span className="required">*</span></label>
                            <Select
                                name="production_item"
                                placeholder="Select Item"
                                options={itemOptions}
                                onChange={handleSelectChange}
                                value={itemOptions.find(i => i.value === form.production_item) || null}
                                className={errors.production_item ? 'is-invalid' : ''}
                            />
                            {errors.production_item && <div className="text-danger fs-8">{errors.production_item}</div>}
                        </div>

                        <div className="col-lg-6 mb-2">
                            <label className="text-black font-w500 fs-7">Item Category</label>
                            <input type="text" className="form-control" value={selectedItemDetails?.itemcategory_name || ''} readOnly />
                        </div>

                        <div className="col-lg-6 mb-2">
                            <label className="text-black font-w500 fs-7">Item Sub Category</label>
                            <input type="text" className="form-control" value={selectedItemDetails?.itemsubcategory_name || ''} readOnly />
                        </div>

                        <div className="col-lg-6 mb-2">
                            <label className="text-black font-w500 fs-7">Product Category<span className="required">*</span></label>
                            <Select
                                name="production_productcategory"
                                placeholder="Select Product Category"
                                options={productOptions}
                                onChange={handleSelectChange}
                                value={productOptions.find(p => p.value === form.production_productcategory) || null}
                                className={errors.production_productcategory ? 'is-invalid' : ''}
                            />
                            {errors.production_productcategory && <div className="text-danger fs-8">{errors.production_productcategory}</div>}
                        </div>

                        {['production_rmcount', 'production_rmcost', 'production_lcost'].map(field => (
                            <div className="col-lg-6 mb-2" key={field}>
                                <label className="text-black font-w500 fs-7">
                                    {field === 'production_rmcount' && 'Raw Material Count'}
                                    {field === 'production_rmcost' && 'Raw Material Amount'}
                                    {field === 'production_lcost' && 'Labour Cost'}
                                    <span className="required">*</span>
                                </label>
                                <input
                                    type="number"
                                    name={field}
                                    className="form-control" 
                                    // className={`form-control ${errors[field] ? 'is-invalid' : ''}`}
                                    value={form[field]}
                                    onChange={handleChange}
                                    onKeyDown={(e) => e.key === '-' && e.preventDefault()}
                                    min="0"
                                    placeholder={`Enter ${field.replace('production_', '').replace('_', ' ')}`}
                                />
                                {errors[field] && <div className="text-danger fs-8">{errors[field]}</div>}
                            </div>
                        ))}

                        <div className="col-lg-6 mb-2">
                            <label className="text-black font-w500 fs-7">Total Cost</label>
                            <input type="text" className="form-control" value={totalCost} readOnly />
                        </div>
                    </div>

                    <div className="d-flex align-items-center justify-content-end gap-3 mt-4 mb-4">
                        <Button type="button" className="btn btn-sm btn-primary" onClick={handleSubmit}>
                            Add Production Cost <i className="fas fa-angle-double-right ms-2"></i>
                        </Button>
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default AddProductionCosting;
